// @flow

import type {
  Languages,
  ActiveLanguages,
  AllowedClients,
} from '../data/GuideTypes';

const LABELS = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
  html: 'HTML',
  react: 'React',
  swift: 'Swift',
  objc: 'Objective-C',
  javaclient: 'Java',
  kotlin: 'Kotlin',
  node: 'Node',
  java: 'Java',
  ruby: 'Ruby',
  python: 'Python',
  php: 'PHP',
  go: 'Go',
  dotnet: '.NET',
  javascript: 'JavaScript',
  js: 'JavaScript',
  esnext: 'ESNext',
  next: 'Next.js',
  m2: 'Reader M2',
  wpe: 'WisePOS E',
  wp3: 'WisePad 3',
  US: 'United States',
  CA: 'Canada',
  GB: 'United Kingdom',
  IE: 'Ireland',
  FR: 'France',
  DE: 'Germany',
  NL: 'Netherlands',
  AU: 'Australia',
  SG: 'Singapore',
  NZ: 'New Zealand',
};

/* Set the default client for the selected platform */
/* Gross because legacy cookies also use lang to store mobile choices */
export const getDefaultServer = (
  serverLang?: string,
  allowedServers?: Array<string>,
) => {
  if (!serverLang || !allowedServers || !allowedServers.includes(serverLang)) {
    return 'node';
  }

  return serverLang;
};

/* Set the default client for the selected platform */
export const getDefaultClient = (
  clientPlatform: string,
  clientLang?: string,
  allowedClients?: AllowedClients,
) => {
  if (!allowedClients) {
    return;
  }

  if (!clientLang || !allowedClients[clientPlatform].includes(clientLang)) {
    return allowedClients[clientPlatform][0];
  }

  return clientLang;
};

export const getDefaultPlatform = (
  platformLang?: string,
  allowedPlatforms?:
    | {[reader: string]: {[country: string]: Array<string>}}
    | Array<string>,
  reader?: string,
  country?: string,
) => {
  if (Array.isArray(allowedPlatforms)) {
    return allowedPlatforms.includes(platformLang)
      ? platformLang
      : allowedPlatforms[0];
  }
  if (
    allowedPlatforms &&
    reader &&
    country &&
    !allowedPlatforms[reader][country].includes(platformLang)
  ) {
    // Return the default platform for the reader, if the user's platform preference is not in the reader platform list
    return allowedPlatforms[reader][country][0];
  }

  return platformLang;
};

export const getDefaultReader = (
  readerLang?: string,
  allowedReaders?: {[country: string]: Array<string>},
  country?: string,
) => {
  if (!allowedReaders) {
    return;
  }

  if (
    allowedReaders &&
    country &&
    !allowedReaders[country].includes(readerLang)
  ) {
    // Return the default platform for the reader, if the user's platform preference is not in the reader platform list
    return allowedReaders[country][0];
  }

  return readerLang;
};

export const getDefaultCountry = (
  countryLang?: string,
  allowedCountries?: Array<string>,
  siteCountry: string,
) => {
  if (!allowedCountries) {
    return;
  }

  if (!countryLang && allowedCountries.includes(siteCountry)) {
    return siteCountry;
  }

  if (!countryLang || !allowedCountries.includes(countryLang)) {
    return allowedCountries[0];
  }

  return countryLang;
};

export const getDefaultLanguage = (
  languages: Languages,
  languagePrefs: ActiveLanguages,
  siteCountry: string,
) => {
  // e.g. platform, client, server
  const languageOptions = Object.keys(languages.allowed);
  const active = {};
  const {
    platform: platformPref,
    client: clientPref,
    server: serverPref,
    reader: readerPref,
    country: countryPref,
  } = languagePrefs;
  const {
    country: allowedCountries,
    reader: allowedReaders,
    platform: allowedPlatforms,
    client: allowedClients,
    server: allowedServers,
  } = languages.allowed;
  languageOptions.forEach((option) => {
    switch (option) {
      case 'country':
        active.country = getDefaultCountry(
          countryPref,
          allowedCountries,
          siteCountry,
        );
        break;
      case 'reader':
        active.reader = getDefaultReader(
          readerPref,
          allowedReaders,
          active.country,
        );
        break;
      case 'platform':
        active.platform = getDefaultPlatform(
          platformPref,
          allowedPlatforms,
          active.reader,
          active.country,
        );
        break;
      case 'client':
        // Pick the right default client based on the selected platform
        // (e.g web has a different set of client languages than ios)
        const clientPlatform = active.platform || platformPref || '';
        active.client = getDefaultClient(
          clientPlatform,
          clientPref,
          allowedClients,
        );
        break;
      case 'server':
        active.server = getDefaultServer(serverPref, allowedServers);
    }
  });

  return {
    ...languages,
    active,
  };
};

export const formatLanguage = (language: string): string => {
  return LABELS[language] || language;
};
